import { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BetSlipContext = createContext({
    selections: [],
    isOpen: false,
    addSelection: () => {},
    removeSelection: () => {},
    clearSlip: () => {},
    toggleOpen: () => {},
    setOpen: () => {},
    placeBet: async () => {}
});

export const useBetSlip = () => useContext(BetSlipContext);

export const BetSlipProvider = ({ children }) => {
    const [selections, setSelections] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const { isAuthenticated } = useAuth();

    const addSelection = useCallback((selection) => {
        if (!isAuthenticated) {
            toast.error('Please login to add selections');
            return;
        }
        
        // Check if race has already started or finished
        if (selection.race_time) {
            const raceTime = new Date(selection.race_time);
            const now = new Date();
            if (raceTime <= now) {
                toast.error('Cannot bet on a race that has already started');
                return;
            }
        }
        
        // Also check status if available
        if (selection.status === 'finished' || selection.status === 'in_progress' || selection.status === 'result') {
            toast.error('Cannot bet on a race that has already started or finished');
            return;
        }
        
        setSelections(prev => {
            // Check if this exact selection already exists
            const exists = prev.find(s => s.entrant_id === selection.entrant_id);
            if (exists) {
                toast.info('Selection already in bet slip');
                return prev;
            }
            
            // Check if there's already a selection from the same race
            const sameRace = prev.find(s => s.event_id === selection.event_id);
            if (sameRace) {
                toast.info(`Already have a selection from this race. Adding anyway for singles.`);
            }
            
            toast.success(`Added ${selection.entrant_name} to bet slip`);
            return [...prev, { 
                ...selection, 
                id: `${selection.entrant_id}-${Date.now()}`,
                added_at: new Date().toISOString()
            }];
        });
        setIsOpen(true);
    }, [isAuthenticated]);

    const removeSelection = useCallback((selectionId) => {
        setSelections(prev => prev.filter(s => s.id !== selectionId));
        toast.info('Selection removed');
    }, []);

    const clearSlip = useCallback(() => {
        setSelections([]);
        toast.info('Bet slip cleared');
    }, []);

    const toggleOpen = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const placeBet = useCallback(async (stakes, betType = 'singles', eachWaySelections = {}) => {
        if (selections.length === 0) {
            toast.error('No selections in bet slip');
            return;
        }

        // Check if any races have already started
        const now = new Date();
        const startedRaces = selections.filter(s => {
            if (s.race_time && new Date(s.race_time) <= now) return true;
            if (s.start_time && new Date(s.start_time) <= now) return true;
            return false;
        });
        
        if (startedRaces.length > 0) {
            toast.error(`Cannot place bet - ${startedRaces.length} race(s) have already started. Please remove them first.`);
            return;
        }

        try {
            // Calculate total stake based on bet type
            let totalStake = 0;
            let betDetails = {};

            if (betType === 'singles') {
                // Singles: each selection has its own stake
                const singlesData = selections.map(s => {
                    const stake = stakes[s.id] || 0;
                    const isEachWay = eachWaySelections[s.id] || false;
                    totalStake += stake;
                    
                    return {
                        entrant_id: s.entrant_id,
                        entrant_name: s.entrant_name,
                        event_id: s.event_id,
                        event_name: s.event_name,
                        stage_name: s.stage_name,
                        odds_decimal: s.odds_decimal,
                        odds_fractional: s.odds_fractional,
                        start_time: s.start_time,
                        stake: stake,
                        each_way: isEachWay,
                        potential_return: isEachWay 
                            ? (stake / 2 * s.odds_decimal) + (stake / 2 * (1 + (s.odds_decimal - 1) / 4))
                            : stake * s.odds_decimal
                    };
                }).filter(s => s.stake > 0);

                betDetails = {
                    type: 'singles',
                    bets: singlesData,
                    total_stake: totalStake
                };
            } else if (betType === 'accumulator') {
                // Accumulator: single stake for all selections
                const accaStake = stakes.accumulator || 0;
                const isEachWay = eachWaySelections.accumulator || false;
                const combinedOdds = selections.reduce((acc, s) => acc * s.odds_decimal, 1);
                
                totalStake = accaStake;
                
                betDetails = {
                    type: 'accumulator',
                    fold: selections.length,
                    selections: selections.map(s => ({
                        entrant_id: s.entrant_id,
                        entrant_name: s.entrant_name,
                        event_id: s.event_id,
                        event_name: s.event_name,
                        stage_name: s.stage_name,
                        odds_decimal: s.odds_decimal,
                        odds_fractional: s.odds_fractional,
                        start_time: s.start_time
                    })),
                    combined_odds: combinedOdds,
                    stake: accaStake,
                    each_way: isEachWay,
                    potential_return: isEachWay
                        ? (accaStake / 2 * combinedOdds) + (accaStake / 2 * (1 + (combinedOdds - 1) / 4))
                        : accaStake * combinedOdds,
                    total_stake: totalStake
                };
            }

            // Post to backend (demo mode)
            const response = await axios.post(`${API}/betslip/place`, betDetails);
            
            const potentialReturn = betType === 'singles'
                ? betDetails.bets.reduce((sum, b) => sum + b.potential_return, 0)
                : betDetails.potential_return;
            
            toast.success(
                <div>
                    <p className="font-semibold">Bet placed successfully!</p>
                    <p className="text-sm">
                        {betType === 'accumulator' 
                            ? `${selections.length}-fold accumulator` 
                            : `${betDetails.bets?.length || 0} single bet(s)`}
                    </p>
                    <p className="text-sm">Potential return: £{potentialReturn.toFixed(2)}</p>
                </div>
            );
            
            setSelections([]);
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to place bet');
            throw error;
        }
    }, [selections]);

    return (
        <BetSlipContext.Provider value={{
            selections,
            isOpen,
            addSelection,
            removeSelection,
            clearSlip,
            toggleOpen,
            setOpen: setIsOpen,
            placeBet
        }}>
            {children}
        </BetSlipContext.Provider>
    );
};
